import { useState, useEffect, useMemo } from "react";
import { Activity, AlertTriangle } from "lucide-react";
import { getParameterIcon } from "../utils/parameterIcons";
import { IWeatherData, ParameterData } from "../types/device";
import { useGetDeviceParameters, useGetMultiDeviceParameters } from "../queries/parameters";
import { useGetMultiDeviceLiveData } from "../queries/admin";
import useMqttStore from "../store/useMqttStore";
import { useDeviceStore } from "../store/useDeviceStore";
import { useValueStore } from "../store/useValueState";
import { ParameterFormData } from "./widgets/table/AddParameterModal";

interface MqttDataDisplayProps {
  liveData: IWeatherData | null;
  isConnected: boolean;
  deviceId: string | null;
  onDetailClick?: (parameter: ParameterData) => void;
  title?: string;
  subtitle?: string;
}

interface AggregatedParameter extends ParameterData {
  deviceCount: number;
  totalDevices: number;
  isAveraged: boolean;
}

export default function MqttDataDisplay({
  liveData,
  isConnected,
  deviceId,
  onDetailClick,
}: MqttDataDisplayProps) {
  const [parameters, setParameters] = useState<AggregatedParameter[]>([]);

  const { deviceDataMap } = useMqttStore();
  const { selectedDeviceIds, selectedDeviceNames } = useDeviceStore();
  const { metrics } = useValueStore();

  const isMultiDevice = selectedDeviceIds.length > 1;

  // Build the effective device ID list the same way the parameter table does:
  // use all selected devices when available, fall back to the single primary device.
  const effectiveDeviceIds =
    selectedDeviceIds.length > 0
      ? selectedDeviceIds
      : deviceId
      ? [deviceId]
      : [];

  // Single-device fetch (used for loading/error states)
  const {
    data: _singleDeviceParams,
    isLoading,
    error,
  } = useGetDeviceParameters(deviceId || undefined, !!deviceId);

  // Multi-device fetch: merges unique parameter definitions across every
  // selected device (deduped by identifier) — exactly like the parameter table.
  const { data: backendParameters } =
    useGetMultiDeviceParameters(effectiveDeviceIds);

  // Last known reading per device, used as a fallback so returning users see
  // their historical values instead of zero while MQTT reconnects/re-subscribes.
  const lastKnownDataMap = useGetMultiDeviceLiveData(effectiveDeviceIds);

  // Calculate parameter status based on value and thresholds
  const calculateStatus = (
    value?: number,
    minValue?: number,
    maxValue?: number
  ): "normal" | "warning" | "critical" => {
    if (value === undefined || (minValue === undefined && maxValue === undefined) || minValue === null || maxValue === null) {
      return "normal";
    }

    if (minValue !== undefined && maxValue !== undefined && minValue !== null && maxValue !== null) {
      const range = maxValue - minValue;
      const warningThreshold = range * 0.8;

      if (value < minValue || value > maxValue) {
        return "critical";
      } else if (value > minValue + warningThreshold || value < minValue + range * 0.2) {
        return "warning";
      }
    }

    return "normal";
  };


  // Selected parameter identifiers for filtering
  const selectedIdentifiers = useMemo(() => {
    if (!backendParameters || !metrics || metrics.length === 0) return null;
    return backendParameters
      .filter((p: ParameterFormData) => metrics.includes(p.name))
      .map((p: ParameterFormData) => p.identifier);
  }, [backendParameters, metrics]);

  // Read a parameter's value for a device: prefer the live MQTT value, and
  // fall back to the last known reading from the DB so returning users see
  // their historical data instead of zero while waiting for the next MQTT
  // message. A device with no live data and no history yet (e.g. brand new)
  // still reads as undefined.
  const readDeviceValue = (dId: string, identifier: string): number | string | undefined => {
    const live = deviceDataMap[dId]?.data as Record<string, any> | undefined;
    if (live && live[identifier] !== undefined && live[identifier] !== null) {
      return live[identifier];
    }
    const lastKnown = lastKnownDataMap[dId] as Record<string, any> | null;
    if (lastKnown && lastKnown[identifier] !== undefined && lastKnown[identifier] !== null) {
      return lastKnown[identifier];
    }
    return undefined;
  };

  // Transform and aggregate parameters across multiple devices
  const transformAndAggregateParameters = (params: ParameterFormData[]): AggregatedParameter[] => {
    // Filter by selected parameters
    const filteredParams = selectedIdentifiers
      ? params.filter((p) => selectedIdentifiers.includes(p.identifier))
      : params;

    if (!isMultiDevice) {
      // Single device mode - same as before
      return filteredParams.map((param) => {
        const liveValue = deviceId ? readDeviceValue(deviceId, param.identifier) : undefined;
        // STRING params surface the raw text ("NA" when there's no reading);
        // numeric params apply calibration and default to 0.
        const value =
          param.dataType === "STRING"
            ? liveValue !== undefined
              ? String(liveValue)
              : "NA"
            : liveValue !== undefined
              ? (liveValue as number) * (param.calibration || 1)
              : 0;
        const status =
          typeof value === "number"
            ? calculateStatus(value, param.minValue, param.maxValue)
            : "normal";

        return {
          id: param.id,
          identifier: param.identifier,
          name: param.name,
          value,
          unit: param.unit,
          dataType: param.dataType,
          deviceId: param.deviceId,
          icon: param.icon,
          minValue: param.minValue,
          maxValue: param.maxValue,
          iconElement: getParameterIcon(param.icon || param.identifier),
          status,
          trend: "stable" as const,
          trendValue: 0,
          target: param.maxValue ? param.maxValue * 0.8 : undefined,
          calibration: param.calibration,
          deviceCount: 1,
          totalDevices: 1,
          isAveraged: false,
        };
      });
    }

    // Multi-device mode - aggregate by identifier
    return filteredParams.map((param) => {
      // STRING params can't be averaged — show the raw text from the first
      // device that reports it, defaulting to "NA" when none do.
      if (param.dataType === "STRING") {
        let stringValue: string | undefined;
        let contributing = 0;
        for (const dId of selectedDeviceIds) {
          const raw = readDeviceValue(dId, param.identifier);
          if (raw !== undefined && raw !== null) {
            if (stringValue === undefined) stringValue = String(raw);
            contributing += 1;
          }
        }
        return {
          id: param.id,
          identifier: param.identifier,
          name: param.name,
          value: stringValue ?? "NA",
          unit: param.unit,
          dataType: param.dataType,
          deviceId: param.deviceId,
          icon: param.icon,
          minValue: param.minValue,
          maxValue: param.maxValue,
          iconElement: getParameterIcon(param.icon || param.identifier),
          status: "normal" as const,
          trend: "stable" as const,
          trendValue: 0,
          target: undefined,
          calibration: param.calibration,
          deviceCount: contributing,
          totalDevices: selectedDeviceIds.length,
          isAveraged: false,
        };
      }

      const values: number[] = [];
      const contributingDevices: string[] = [];

      for (const dId of selectedDeviceIds) {
        const raw = readDeviceValue(dId, param.identifier);
        if (raw !== undefined && typeof raw === "number") {
          values.push(raw * (param.calibration || 1));
          contributingDevices.push(dId);
        }
      }

      const deviceCount = values.length;
      const avgValue = deviceCount > 0 ? values.reduce((sum, v) => sum + v, 0) / deviceCount : 0;
      const status = calculateStatus(avgValue, param.minValue, param.maxValue);

      return {
        id: param.id,
        identifier: param.identifier,
        name: param.name,
        value: avgValue,
        unit: param.unit,
        dataType: param.dataType,
        deviceId: param.deviceId,
        icon: param.icon,
        minValue: param.minValue,
        maxValue: param.maxValue,
        iconElement: getParameterIcon(param.icon || param.identifier),
        status,
        trend: "stable" as const,
        trendValue: 0,
        target: param.maxValue ? param.maxValue * 0.8 : undefined,
        calibration: param.calibration,
        deviceCount,
        totalDevices: selectedDeviceIds.length,
        isAveraged: deviceCount > 1,
      };
    });
  };

  // Update parameters when backend data, live data, or the last-known-value
  // fallback changes
  useEffect(() => {
    if (backendParameters) {
      setParameters(transformAndAggregateParameters(backendParameters));
    }
  }, [
    backendParameters,
    deviceDataMap,
    deviceId,
    selectedDeviceIds,
    selectedIdentifiers,
    lastKnownDataMap,
  ]);

  // Real-time updates for live values from MQTT
  useEffect(() => {
    const hasData = isMultiDevice
      ? selectedDeviceIds.some((id) => deviceDataMap[id]?.data)
      : deviceId && deviceDataMap[deviceId]?.data;

    const interval = setInterval(() => {
      if (backendParameters && hasData) {
        setParameters(transformAndAggregateParameters(backendParameters));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [backendParameters, deviceDataMap, deviceId, selectedDeviceIds, selectedIdentifiers]);

  if (!deviceId) {
    return (
      <div className="bg-transparent rounded-lg border border-gray-200 p-4">
        <p className="text-gray-500 text-center">Please select a device to view live data</p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-transparent via-indigo-50/40 to-sky-50/40 rounded-xl border border-indigo-100 shadow-md ring-1 ring-indigo-100/50 overflow-hidden">
      {isLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading parameters...</p>
        </div>
      ) : error ? (
        <div className="text-center py-8">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">Error loading parameters</p>
          <p className="text-sm text-gray-500 mt-2">{error.message}</p>
        </div>
      ) : parameters.length > 0 ? (
        <div className="flex items-stretch px-2">
          {parameters.map((parameter, index) => {
            const isStringValue = typeof parameter.value === "string";
            const value = isStringValue ? (parameter.value as string) : parameter.value || 0;
            return (
              <div
                key={parameter.id || parameter.identifier}
                className="flex-1 flex items-center min-w-0"
              >
                <div
                  className="flex-1 px-4 py-5 min-w-0"
                  onClick={() => onDetailClick?.(parameter)}
                >
                  <div className="mb-2">
                    <p className="text-sm text-indigo-600/80 font-medium truncate">
                      {parameter.name}
                    </p>
                  </div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-mono text-2xl font-semibold text-gray-900">
                      {isStringValue
                        ? (value as string)
                        : (value as number).toLocaleString("en-US", {
                            minimumFractionDigits: (value as number) % 1 === 0 ? 0 : 2,
                            maximumFractionDigits: 2,
                          })}
                    </span>
                    <span className="text-sm text-gray-400 font-medium">
                      {parameter.unit || ""}
                    </span>
                    {isMultiDevice && parameter.isAveraged && (
                      <span className="text-xs text-indigo-500 font-medium ml-1">avg</span>
                    )}
                  </div>
                </div>
                {index < parameters.length - 1 && (
                  <div className="w-px self-stretch my-3 bg-gradient-to-b from-transparent via-indigo-200 to-transparent flex-shrink-0" />
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-6">
          <Activity className="w-6 h-6 mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-500">No parameters configured</p>
        </div>
      )}
    </div>
  );
}
